// Local backup / restore of the whole POS dataset to a user-chosen device
// folder, via the native Android FolderBackupPlugin (Storage Access Framework).
//
// All app state lives in IndexedDB under the `sconnect.` prefix. We serialise
// every such key into one JSON file and write it into a folder the user picked
// (e.g. an SD card, Downloads, or a synced Drive folder). Restore reads any such
// JSON file back. This is the on-device counterpart to services/cloudBackup.ts.
//
// On the web (no native shell) the folder picker isn't available; callers should
// fall back to the browser download/upload flow in Settings (export/import).

import { Capacitor, registerPlugin } from '@capacitor/core';
import { idbAllKeys, idbGetRaw, idbSetRaw, idbDel, cachedRawKey } from './idb';

interface FolderBackupNativePlugin {
  getFolder(): Promise<{ uri: string; name: string }>;
  pickFolder(): Promise<{ uri: string; name: string }>;
  writeBackup(opts: { filename: string; content: string }): Promise<{ uri: string; name: string }>;
  pruneBackups(opts: { prefix: string; keep: number }): Promise<{ deleted: number }>;
  pickAndRead(): Promise<{ content: string; name: string }>;
}

const Native = registerPlugin<FolderBackupNativePlugin>('FolderBackup');

const PREFIX = 'sconnect.';

// Naming + retention for the JSON snapshots written into the backup folder.
const FILE_PREFIX = 'epos-backup-';
const KEEP_BACKUPS = 5;

// True when running inside the Android (or iOS) Capacitor shell, where the
// native folder picker exists.
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

// Snapshot every POS collection currently stored.
async function collectLocalData(): Promise<Record<string, unknown>> {
  const dump: Record<string, unknown> = {};
  for (const key of await idbAllKeys()) {
    if (!key.startsWith(PREFIX)) continue;
    const raw = await idbGetRaw(key);
    if (raw == null) continue;
    try {
      dump[key] = JSON.parse(raw);
    } catch {
      /* skip non-JSON values */
    }
  }
  return dump;
}

// Overwrite local POS collections with a snapshot. Existing `sconnect.` keys not
// present in the snapshot are removed so local state matches the backup exactly.
async function applyLocalData(data: Record<string, unknown>): Promise<void> {
  for (const k of await idbAllKeys()) {
    if (k.startsWith(PREFIX)) await idbDel(k);
  }
  for (const [k, v] of Object.entries(data)) {
    if (k.startsWith(PREFIX)) await idbSetRaw(k, JSON.stringify(v));
  }
}

// The currently chosen backup folder's display name, or '' if none picked yet.
export async function getBackupFolder(): Promise<string> {
  const { name } = await Native.getFolder();
  return name;
}

// Show the system folder picker; returns the chosen folder's display name.
export async function chooseBackupFolder(): Promise<string> {
  const { name } = await Native.pickFolder();
  return name;
}

// Write a timestamped JSON snapshot into the chosen folder, then prune the
// folder to the most recent KEEP_BACKUPS snapshots. Returns the file name
// written. Throws (code 'NO_FOLDER') if no folder has been chosen.
export async function backupToFolder(): Promise<string> {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').split('T');
  const filename = `${FILE_PREFIX}${stamp[0]}_${stamp[1].slice(0, 8)}.json`;
  const content = JSON.stringify(await collectLocalData(), null, 2);
  const { name } = await Native.writeBackup({ filename, content });
  // Keep only the newest few snapshots; best-effort so a failed prune never
  // fails the backup that just succeeded.
  try {
    await Native.pruneBackups({ prefix: FILE_PREFIX, keep: KEEP_BACKUPS });
  } catch (err) {
    console.warn('Backup prune failed:', err);
  }
  return name;
}

// Show the system file picker, read the chosen JSON backup and apply it locally.
// Returns the restored file's name.
export async function restoreFromFolder(): Promise<string> {
  const { content, name } = await Native.pickAndRead();
  const data = JSON.parse(content) as Record<string, unknown>;
  await applyLocalData(data);
  return name;
}

// ---- Automatic folder backup ----
//
// When enabled (and a folder is chosen), a fresh snapshot is written to the
// backup folder on app launch and periodically, but no more often than the
// chosen interval. Settings live outside the `sconnect.` namespace so a restore
// doesn't clobber them.

const autoEnabledCache = cachedRawKey('epos.autoBackup.enabled', '0');
const autoIntervalCache = cachedRawKey('epos.autoBackup.intervalHours', '');
const autoLastCache = cachedRawKey('epos.autoBackup.lastRun', '');

export interface AutoBackupSettings {
  enabled: boolean;
  // How long to wait between automatic backups, in hours (24 = daily).
  intervalHours: number;
}

export function getAutoBackupSettings(): AutoBackupSettings {
  return {
    enabled: autoEnabledCache.get() === '1',
    intervalHours: Number(autoIntervalCache.get()) || 24,
  };
}

export function setAutoBackupSettings(s: AutoBackupSettings): void {
  autoEnabledCache.set(s.enabled ? '1' : '0');
  autoIntervalCache.set(String(s.intervalHours));
}

// ISO timestamp of the last automatic backup, or '' if none has run yet.
export function getLastAutoBackup(): string {
  return autoLastCache.get();
}

// Run a folder backup if: we're on native, auto-backup is enabled, a folder is
// chosen, and the configured interval has elapsed since the last auto-backup.
// Returns the written file name if a backup ran, else null. Never throws —
// auto-backup failures are logged and swallowed so they don't disrupt the app.
export async function maybeAutoBackup(): Promise<string | null> {
  if (!isNativeApp()) return null;
  const { enabled, intervalHours } = getAutoBackupSettings();
  if (!enabled) return null;

  const last = getLastAutoBackup();
  if (last && Date.now() - new Date(last).getTime() < intervalHours * 3_600_000) {
    return null;
  }

  try {
    // No folder chosen yet → nothing to write to.
    const { name: folder } = await Native.getFolder();
    if (!folder) return null;
    const name = await backupToFolder();
    autoLastCache.set(new Date().toISOString());
    return name;
  } catch (err) {
    console.warn('Auto-backup failed:', err);
    return null;
  }
}
